import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import des fichiers de traduction
import frPublic from './locales/fr/public.json';
import enPublic from './locales/en/public.json';
import frClient from './locales/fr/client.json';
import enClient from './locales/en/client.json';
import frPartner from './locales/fr/partner.json';
import enPartner from './locales/en/partner.json';
import frAdmin from './locales/fr/admin.json';
import enAdmin from './locales/en/admin.json';
import brand from './config/brand';
import dnaEn from './locales/dna/en.json';
import dnaFr from './locales/dna/fr.json';

// Ressources de traduction
const resources = {
  fr: {
    public: frPublic,
    client: frClient,
    partner: frPartner,
    admin: frAdmin
  },
  en: {
    public: enPublic,
    client: enClient,
    partner: enPartner,
    admin: enAdmin
  }
};

// Détection : français par défaut (app FR), bascule EN/FR mémorisée via localStorage.
// On n'utilise PAS 'navigator' afin d'éviter qu'un navigateur en anglais force l'anglais dans le CRM FR.
const detectionOrder = ['localStorage'];

// Initialiser i18n de manière synchrone
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: brand.defaultLocale || 'fr',
    defaultNS: 'public',
    ns: ['public', 'client', 'partner', 'admin'],
    
    // Options de détection
    detection: {
      order: detectionOrder,
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage']
    },
    
    interpolation: {
      escapeValue: false, // React gère déjà l'échappement
      // Nom de marque injecté globalement → {{brandName}}, {{brandLegal}}, {{brandShort}}
      // se résolvent automatiquement selon la marque active (ASAR / DNA-24).
      defaultVariables: {
        brandName: brand.name,
        brandLegal: brand.legalName,
        brandShort: (brand.meta && brand.meta.shortName) || brand.name,
      }
    },
    
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed'
    }
  });

// Surcharges de contenu spécifiques à la marque DNA-24 (white-label)
// + langue par défaut ANGLAIS pour DNA (même en France).
// Le français n'est appliqué QUE si l'utilisateur l'a explicitement choisi via le bouton FR/EN
// (mémorisé dans la clé dédiée « dnaLang », distincte de la détection automatique).
if (brand.key === 'dna') {
  i18n.addResourceBundle('en', 'public', dnaEn, true, true);
  i18n.addResourceBundle('fr', 'public', dnaFr, true, true);
  const userChoice = localStorage.getItem('dnaLang');
  const initialLang = userChoice === 'fr' || userChoice === 'en' ? userChoice : 'en';
  i18n.changeLanguage(initialLang);
}

// Fonction pour détecter le pays via l'API de géolocalisation (appelée après init)
const detectCountryLanguage = async () => {
  // DNA : anglais par défaut, pas de bascule géographique automatique.
  if (brand.key === 'dna') {
    return;
  }
  // Ne pas re-détecter si l'utilisateur a déjà choisi une langue
  if (localStorage.getItem('i18nextLng')) {
    return;
  }
  
  try {
    const response = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
    if (response.ok) {
      const data = await response.json();
      const countryCode = data.country_code;
      
      // Pays francophones
      const frenchCountries = [
        'FR', 'BE', 'CH', 'CA', 'LU', 'MC', 'SN', 'CI', 'ML', 'BF', 
        'NE', 'TG', 'BJ', 'GA', 'CG', 'CD', 'CM', 'TD', 'CF', 'GN',
        'MG', 'HT', 'MU', 'RE', 'GP', 'MQ', 'GF', 'NC', 'PF', 'WF',
        'PM', 'YT', 'BL', 'MF', 'DZ', 'MA', 'TN'
      ];
      
      const detectedLang = frenchCountries.includes(countryCode) ? 'fr' : 'en';
      i18n.changeLanguage(detectedLang);
    }
  } catch (error) {
    // Géolocalisation indisponible : on conserve la langue par défaut.
  }
};

// Lancer la détection géographique après un court délai
setTimeout(() => {
  detectCountryLanguage();
}, 500);

// Persiste la préférence de langue en base pour l'utilisateur connecté (best-effort).
// No-op si aucun token (site public / login non authentifié) → n'impacte pas le site vitrine.
const persistLanguageToBackend = (lng) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;
    const api = process.env.REACT_APP_BACKEND_URL;
    if (!api) return;
    const norm = (lng || '').toLowerCase().split('-')[0];
    if (norm !== 'fr' && norm !== 'en') return;
    fetch(`${api}/api/auth/language`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ language: norm })
    }).catch(() => {});
  } catch (e) {
    // best-effort : un échec de persistance ne doit jamais bloquer le changement de langue
  }
};

// Fonction utilitaire pour changer la langue
export const changeLanguage = (lng) => {
  i18n.changeLanguage(lng);
  localStorage.setItem('i18nextLng', lng);
  // DNA : on mémorise le choix EXPLICITE de l'utilisateur (clé dédiée) pour le réappliquer aux prochaines visites.
  if (brand.key === 'dna') {
    localStorage.setItem('dnaLang', lng);
  }
  // Persistance en base (par utilisateur) si connecté.
  persistLanguageToBackend(lng);
};

// Fonction pour obtenir la langue actuelle
export const getCurrentLanguage = () => {
  return i18n.language || 'fr';
};

// Synchronise l'attribut <html lang> avec la langue active (SEO / accessibilité).
const syncHtmlLang = (lng) => {
  if (typeof document !== 'undefined' && lng) {
    document.documentElement.lang = lng.startsWith('fr') ? 'fr' : 'en';
  }
};
syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
